import React, { useMemo, useState } from "react";
import { Button, Group, Modal, Stack, Text, Title } from "@mantine/core";
import { notifyError } from "../../utils/notify";
import { useApp } from "../../context/AppContext";
import PersonalizeForm from "./wizard/PersonalizeForm";
import ValueRewardsForm from "./wizard/ValueRewardsForm";
import ActionRewardsForm from "./wizard/ActionRewardsForm";
import {
  PersonalizeSummaryCard,
  ValueRewardsSummaryCard,
  ActionRewardsSummaryCard,
} from "./wizard/SummarySections";
import {
  PersonalizeDraft,
  ActionRewardsDraft,
  RedemptionIncrement,
  ValueRewardsDraft,
  DEFAULT_REDEEM_CASH_VALUE,
  DEFAULT_REDEEM_POINTS_COST,
  EARN_POINTS,
  EARN_PER_AMOUNT,
  ROLLING_EXPIRATION_DAYS,
  isActionRewardsValid,
  redeemRewardName,
  isValueRewardsValid,
  toNumber,
} from "./wizard/types";
import {
  LoyaltySettings,
  LoyaltyEarnRule,
  LoyaltyReward,
  LoyaltyEarnRuleType,
  UpdateLoyaltySettingsRequest,
} from "../../types/loyalty";

interface SettingsTabProps {
  businessLocationId: string;
  settings: LoyaltySettings;
  earnRules: LoyaltyEarnRule[];
  rewards: LoyaltyReward[];
  /** Refetch settings + earn rules + rewards after a write. */
  onRefresh: () => Promise<void>;
}

type EditSection = "personalize" | "value" | "action" | null;

function settingsBody(
  settings: LoyaltySettings,
  patch: Partial<UpdateLoyaltySettingsRequest>,
): UpdateLoyaltySettingsRequest {
  return {
    enabled: settings.enabled,
    preset: settings.preset,
    deduct_redemption_from_commission:
      settings.deduct_redemption_from_commission,
    points_expiration_policy: settings.points_expiration_policy,
    points_expiration_days: settings.points_expiration_days ?? null,
    ...patch,
  };
}

function showSaveError(error: any) {
  console.error("Error saving loyalty settings:", error);
  const serverMessage =
    error?.status >= 400 && error?.status < 500 && error?.message
      ? error.message
      : "Could not save changes. Please try again.";
  notifyError(serverMessage, { title: "Save failed" });
}

/**
 * Rewards → Settings (loyalty FE1 §3.4) — the live edit surface after
 * Publish. Read-only section cards (same three sections as Review & Publish)
 * with per-section Edit modals that reuse the wizard step forms, plus the
 * whole-program enable/disable toggle.
 */
export default function SettingsTab({
  businessLocationId,
  settings,
  earnRules,
  rewards,
  onRefresh,
}: SettingsTabProps) {
  const {
    getLoyaltyEarnRules,
    getLoyaltyRewards,
    updateLoyaltySettings,
    createLoyaltyEarnRule,
    updateLoyaltyEarnRule,
    createLoyaltyReward,
    updateLoyaltyReward,
  } = useApp();

  const [editSection, setEditSection] = useState<EditSection>(null);
  const [isSaving, setIsSaving] = useState(false);

  const serviceSpendRule = useMemo(
    () =>
      earnRules.find(
        (rule) =>
          rule.rule_type === "service_spend" && rule.is_active !== false,
      ),
    [earnRules],
  );
  const retailSpendRule = useMemo(
    () =>
      earnRules.find(
        (rule) => rule.rule_type === "retail_spend" && rule.is_active !== false,
      ),
    [earnRules],
  );
  const servicesRate = serviceSpendRule
    ? toNumber(serviceSpendRule.points, EARN_POINTS)
    : EARN_POINTS;
  const productsRate = retailSpendRule
    ? toNumber(retailSpendRule.points, EARN_POINTS)
    : EARN_POINTS;
  const rebookingRule = useMemo(
    () => earnRules.find((rule) => rule.rule_type === "rebooking"),
    [earnRules],
  );
  // nth_visit splits on interval: interval_count 1 backs First appointment
  // (event-based, once per client); > 1 is the plain Visits rule.
  const nthVisitRule = useMemo(
    () =>
      earnRules.find(
        (rule) =>
          rule.rule_type === "nth_visit" &&
          toNumber(rule.interval_count ?? 1, 1) > 1,
      ),
    [earnRules],
  );
  const firstVisitRule = useMemo(
    () =>
      earnRules.find(
        (rule) =>
          rule.rule_type === "nth_visit" &&
          toNumber(rule.interval_count ?? 1, 1) === 1,
      ),
    [earnRules],
  );

  // === Server data → wizard-form drafts ===================================
  const personalizeFromServer = useMemo<PersonalizeDraft>(
    () => ({
      preset: settings.preset,
      expirationPolicy: settings.points_expiration_policy,
      expirationDays:
        settings.points_expiration_days ?? ROLLING_EXPIRATION_DAYS,
      // Commission-deduction question removed from the UI (Aug-25 punch
      // list); the underlying flag is preserved from the server so a Save
      // never accidentally overwrites a value set outside this UI.
      deductFromCommission: settings.deduct_redemption_from_commission,
    }),
    [settings],
  );

  // The single "managed" reward is the lowest-points_cost active reward with
  // a positive cash_value. Anything else the salon has (inactive, zero-value,
  // extra rows from an older multi-increment setup) is left untouched by
  // saves — the reconciliation below only writes this one row.
  const managedReward = useMemo<RedemptionIncrement | null>(() => {
    const sorted = rewards
      .filter((r) => r.is_active !== false && toNumber(r.cash_value) > 0)
      .slice()
      .sort((a, b) => toNumber(a.points_cost) - toNumber(b.points_cost));
    if (sorted.length === 0) return null;
    const first = sorted[0];
    return {
      uuid: first.uuid,
      cashValue: toNumber(first.cash_value),
      pointsCost: toNumber(first.points_cost),
    };
  }, [rewards]);
  const valueRewardsFromServer = useMemo<ValueRewardsDraft>(
    () => ({
      servicesRate,
      productsRate,
      redemptionIncrement: managedReward ?? {
        cashValue: DEFAULT_REDEEM_CASH_VALUE,
        pointsCost: DEFAULT_REDEEM_POINTS_COST,
      },
    }),
    [managedReward, servicesRate, productsRate],
  );

  const actionRewardsFromServer = useMemo<ActionRewardsDraft>(
    () => ({
      rebookingEnabled:
        rebookingRule != null && rebookingRule.is_active !== false,
      rebookingPoints: rebookingRule ? toNumber(rebookingRule.points, 50) : 50,
      visitsEnabled: nthVisitRule != null && nthVisitRule.is_active !== false,
      visitsPoints: nthVisitRule ? toNumber(nthVisitRule.points, 50) : 50,
      visitsInterval: nthVisitRule
        ? toNumber(nthVisitRule.interval_count ?? 3, 3)
        : 3,
      firstAppointmentEnabled:
        firstVisitRule != null && firstVisitRule.is_active !== false,
      firstAppointmentPoints: firstVisitRule
        ? toNumber(firstVisitRule.points, 50)
        : 50,
    }),
    [rebookingRule, nthVisitRule, firstVisitRule],
  );

  // Edit-modal drafts (seeded on open).
  const [personalizeDraft, setPersonalizeDraft] = useState<PersonalizeDraft>(
    personalizeFromServer,
  );
  const [valueDraft, setValueDraft] = useState<ValueRewardsDraft>(
    valueRewardsFromServer,
  );
  const [actionDraft, setActionDraft] = useState<ActionRewardsDraft>(
    actionRewardsFromServer,
  );

  const openEdit = (section: Exclude<EditSection, null>) => {
    if (section === "personalize") setPersonalizeDraft(personalizeFromServer);
    if (section === "value") setValueDraft(valueRewardsFromServer);
    if (section === "action") setActionDraft(actionRewardsFromServer);
    setEditSection(section);
  };

  const closeEdit = () => {
    if (!isSaving) setEditSection(null);
  };

  // === Writes =============================================================

  /** PUT settings, then reconcile spend earn-rules to the chosen preset so a
   *  preset switch doesn't leave stale spend rules behind. Rates themselves
   *  are edited in the value section. */
  const savePersonalize = async () => {
    setIsSaving(true);
    try {
      await updateLoyaltySettings(
        businessLocationId,
        settingsBody(settings, {
          preset: personalizeDraft.preset,
          deduct_redemption_from_commission:
            personalizeDraft.deductFromCommission,
          points_expiration_policy: personalizeDraft.expirationPolicy,
          points_expiration_days:
            personalizeDraft.expirationPolicy === "rolling"
              ? toNumber(
                  personalizeDraft.expirationDays,
                  ROLLING_EXPIRATION_DAYS,
                )
              : null,
        }),
      );

      const wantedTypes: LoyaltyEarnRuleType[] =
        personalizeDraft.preset === "both"
          ? ["service_spend", "retail_spend"]
          : personalizeDraft.preset === "retail"
            ? ["retail_spend"]
            : ["service_spend"];
      // Re-fetch instead of using the earnRules prop: after a partial failure
      // the prop is stale, and a retry would re-POST rules the previous
      // attempt already created.
      const freshRules = await getLoyaltyEarnRules(businessLocationId);
      const existingSpend = freshRules.filter(
        (rule) =>
          rule.rule_type === "service_spend" ||
          rule.rule_type === "retail_spend",
      );
      for (const wanted of wantedTypes) {
        const existing = existingSpend.find((r) => r.rule_type === wanted);
        if (!existing) {
          await createLoyaltyEarnRule(businessLocationId, {
            rule_type: wanted,
            points: wanted === "service_spend" ? servicesRate : productsRate,
            per_amount: EARN_PER_AMOUNT,
          });
        } else if (existing.is_active === false) {
          await updateLoyaltyEarnRule(businessLocationId, existing.uuid, {
            is_active: true,
          });
        }
      }
      for (const existing of existingSpend) {
        if (
          !wantedTypes.includes(existing.rule_type) &&
          existing.is_active !== false
        ) {
          await updateLoyaltyEarnRule(businessLocationId, existing.uuid, {
            is_active: false,
          });
        }
      }

      // Close the modal FIRST so it doesn't linger during the refetch;
      // the surrounding LoadingOverlay covers the reload.
      setEditSection(null);
      await onRefresh();
    } catch (error) {
      showSaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  /** Writes the split earn rates into the preset's spend rules, then
   *  reconciles the single redemption reward against the server (create if
   *  missing, update if changed, no-op if identical). Other rewards the
   *  salon has (extra active rows, inactive rows, zero-value rows) are
   *  left untouched. */
  const saveValueRewards = async () => {
    setIsSaving(true);
    try {
      const wantedTypes: LoyaltyEarnRuleType[] =
        settings.preset === "both"
          ? ["service_spend", "retail_spend"]
          : settings.preset === "retail"
            ? ["retail_spend"]
            : ["service_spend"];
      // Fresh GET so a retry after partial failure sees rules the previous
      // attempt already created.
      const freshRules = await getLoyaltyEarnRules(businessLocationId);
      for (const wanted of wantedTypes) {
        const points = toNumber(
          wanted === "service_spend"
            ? valueDraft.servicesRate
            : valueDraft.productsRate,
        );
        const existing = freshRules.find((r) => r.rule_type === wanted);
        if (!existing) {
          await createLoyaltyEarnRule(businessLocationId, {
            rule_type: wanted,
            points,
            per_amount: EARN_PER_AMOUNT,
          });
        } else {
          const patch: { points?: number; is_active?: boolean } = {};
          if (toNumber(existing.points) !== points) patch.points = points;
          if (existing.is_active === false) patch.is_active = true;
          if (Object.keys(patch).length > 0) {
            await updateLoyaltyEarnRule(
              businessLocationId,
              existing.uuid,
              patch,
            );
          }
        }
      }

      // Fresh GET of rewards so a retry after partial failure never
      // re-creates a row the previous attempt already posted.
      const freshRewards = await getLoyaltyRewards(businessLocationId);
      const row = valueDraft.redemptionIncrement;
      const pointsCost = toNumber(row.pointsCost);
      const cashValue = toNumber(row.cashValue);
      const body = {
        name: redeemRewardName(pointsCost, cashValue),
        points_cost: pointsCost,
        cash_value: cashValue,
      };
      // Single-increment model: use the draft's uuid if it still exists,
      // else fall back to ANY active reward (heals location state left by
      // the old multi-increment model). Deactivate every other active
      // reward so the checkout picker never shows duplicates.
      const activeRewards = freshRewards.filter((r) => r.is_active !== false);
      const target =
        (row.uuid && activeRewards.find((r) => r.uuid === row.uuid)) ||
        activeRewards[0] ||
        undefined;
      if (!target) {
        await createLoyaltyReward(businessLocationId, body);
      } else {
        const currentCash = toNumber(target.cash_value);
        const currentPoints = toNumber(target.points_cost);
        if (currentCash !== cashValue || currentPoints !== pointsCost) {
          await updateLoyaltyReward(businessLocationId, target.uuid, body);
        }
        // Retire any extra active rewards left over from earlier saves.
        for (const extra of activeRewards) {
          if (extra.uuid !== target.uuid) {
            await updateLoyaltyReward(businessLocationId, extra.uuid, {
              is_active: false,
            });
          }
        }
      }
      // Close the modal FIRST so it doesn't linger during the refetch;
      // the surrounding LoadingOverlay covers the reload.
      setEditSection(null);
      await onRefresh();
    } catch (error) {
      showSaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  /** Rebooking / nth_visit / first appointment: POST when newly enabled, PUT
   *  to update or to flip is_active on toggle-off (rules are kept, not
   *  deleted — §2.3). First appointment is a capped nth_visit rule
   *  (interval_count 1, max_awards_per_client 1). Only these three rules are
   *  touched — any other rule the salon has is left alone. */
  const saveActionRewards = async () => {
    setIsSaving(true);
    try {
      // Rebooking (always unlimited)
      if (actionDraft.rebookingEnabled) {
        if (!rebookingRule) {
          await createLoyaltyEarnRule(businessLocationId, {
            rule_type: "rebooking",
            points: toNumber(actionDraft.rebookingPoints),
          });
        } else {
          await updateLoyaltyEarnRule(businessLocationId, rebookingRule.uuid, {
            points: toNumber(actionDraft.rebookingPoints),
            is_active: true,
          });
        }
      } else if (rebookingRule && rebookingRule.is_active !== false) {
        await updateLoyaltyEarnRule(businessLocationId, rebookingRule.uuid, {
          is_active: false,
        });
      }

      // Visits (nth_visit with interval > 1)
      if (actionDraft.visitsEnabled) {
        if (!nthVisitRule) {
          await createLoyaltyEarnRule(businessLocationId, {
            rule_type: "nth_visit",
            points: toNumber(actionDraft.visitsPoints),
            interval_count: toNumber(actionDraft.visitsInterval, 1),
          });
        } else {
          await updateLoyaltyEarnRule(businessLocationId, nthVisitRule.uuid, {
            points: toNumber(actionDraft.visitsPoints),
            interval_count: toNumber(actionDraft.visitsInterval, 1),
            is_active: true,
          });
        }
      } else if (nthVisitRule && nthVisitRule.is_active !== false) {
        await updateLoyaltyEarnRule(businessLocationId, nthVisitRule.uuid, {
          is_active: false,
        });
      }

      // First appointment (nth_visit with interval_count 1, capped at 1)
      if (actionDraft.firstAppointmentEnabled) {
        if (!firstVisitRule) {
          await createLoyaltyEarnRule(businessLocationId, {
            rule_type: "nth_visit",
            points: toNumber(actionDraft.firstAppointmentPoints),
            interval_count: 1,
            max_awards_per_client: 1,
          });
        } else {
          await updateLoyaltyEarnRule(businessLocationId, firstVisitRule.uuid, {
            points: toNumber(actionDraft.firstAppointmentPoints),
            interval_count: 1,
            max_awards_per_client: 1,
            is_active: true,
          });
        }
      } else if (firstVisitRule && firstVisitRule.is_active !== false) {
        await updateLoyaltyEarnRule(businessLocationId, firstVisitRule.uuid, {
          is_active: false,
        });
      }

      // Close the modal FIRST so it doesn't linger during the refetch;
      // the surrounding LoadingOverlay covers the reload.
      setEditSection(null);
      await onRefresh();
    } catch (error) {
      showSaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={3}>Settings</Title>
          <Text size="sm" c="dimmed">
            Control your program rules, points system, and perks all in one
            place.
          </Text>
        </Stack>
        {/* Program-enabled toggle removed per 2026-08-26 feedback — once
            the wizard flips a location on, it stays on. Disabling the whole
            program would strand every existing points balance mid-flight.
            Individual rules can still be paused via their own toggles. */}
      </Group>

      <PersonalizeSummaryCard
        value={personalizeFromServer}
        servicesRate={servicesRate}
        productsRate={productsRate}
        onEdit={() => openEdit("personalize")}
      />
      <ValueRewardsSummaryCard
        value={valueRewardsFromServer}
        preset={settings.preset}
        onEdit={() => openEdit("value")}
      />
      <ActionRewardsSummaryCard
        value={actionRewardsFromServer}
        onEdit={() => openEdit("action")}
      />

      <Modal
        opened={editSection === "personalize"}
        onClose={closeEdit}
        title={<Title order={4}>Personalize your program</Title>}
        size="56rem"
      >
        <Stack gap="lg">
          <PersonalizeForm
            value={personalizeDraft}
            onChange={setPersonalizeDraft}
            servicesRate={servicesRate}
            productsRate={productsRate}
            showHeading={false}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeEdit} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={savePersonalize}
              loading={isSaving}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={editSection === "value"}
        onClose={closeEdit}
        title={<Title order={4}>Transaction Based Rewards</Title>}
        size="56rem"
      >
        <Stack gap="lg">
          <ValueRewardsForm
            value={valueDraft}
            onChange={setValueDraft}
            preset={settings.preset}
            showHeading={false}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeEdit} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={saveValueRewards}
              loading={isSaving}
              disabled={!isValueRewardsValid(valueDraft, settings.preset)}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={editSection === "action"}
        onClose={closeEdit}
        title={<Title order={4}>Action Based Rewards</Title>}
        size="56rem"
      >
        <Stack gap="lg">
          <ActionRewardsForm
            value={actionDraft}
            onChange={setActionDraft}
            showHeading={false}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={closeEdit} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={saveActionRewards}
              loading={isSaving}
              disabled={!isActionRewardsValid(actionDraft)}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
